import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { RenderModelCode, RenderStyleCode } from '@erp/shared';
import { api, apiBlob } from './client';

/** QUEUED — кадр серии по фото ждёт утверждения эталона. */
export type RenderStatus = 'QUEUED' | 'PENDING' | 'SAVING' | 'DONE' | 'FAILED';

export interface RoomRender {
  id: string;
  roomId: string;
  /** Кадр серии по фото с объекта; у визуализаций 3D-вида — null. */
  seriesId: string | null;
  /** Своё фото, загруженное без генерации, — живёт в окне «Дизайн по фото». */
  uploaded: boolean;
  status: RenderStatus;
  style: RenderStyleCode | null;
  /** null — картинка сделана до появления выбора нейросети. */
  model: RenderModelCode | null;
  wishes: string | null;
  error: string | null;
  /** Меняется при перегенерации — по нему перекачивается картинка. */
  imageVersion: string | null;
  inProposal: boolean;
  createdAt: string;
}

export interface RenderCreateInput {
  roomId: string;
  /** Снимок 3D-вида, data URL JPEG. */
  image: string;
  width: number;
  height: number;
  style?: RenderStyleCode | null;
  model: RenderModelCode;
  wishes?: string | null;
}

export interface RenderSeries {
  id: string;
  roomId: string;
  style: RenderStyleCode | null;
  model: RenderModelCode;
  wishes: string | null;
  /** Кадр-эталон — главное фото серии. */
  referenceId: string | null;
  createdAt: string;
}

export interface RenderSeriesCreateInput {
  roomId: string;
  style?: RenderStyleCode | null;
  model: RenderModelCode;
  wishes?: string | null;
}

export const rendersApi = {
  list: (estimateId: string, roomId?: string) =>
    api.get<{ items: RoomRender[] }>(
      `/estimates/${estimateId}/renders${roomId ? `?roomId=${encodeURIComponent(roomId)}` : ''}`,
    ),
  create: (estimateId: string, input: RenderCreateInput) =>
    api.post<{ render: RoomRender }>(`/estimates/${estimateId}/renders`, input),
  /** Своё фото в КП — data URL JPEG, уже уменьшенное compressPhoto. */
  upload: (estimateId: string, roomId: string, image: string) =>
    api.post<{ render: RoomRender }>(`/estimates/${estimateId}/room-photos`, { roomId, image }),
  setInProposal: (id: string, inProposal: boolean) => api.patch<{ render: RoomRender }>(`/renders/${id}`, { inProposal }),
  remove: (id: string) => api.delete<void>(`/renders/${id}`),
  /** Первый запуск эталона серии или перегенерация картинки. */
  generate: (id: string) => api.post<{ render: RoomRender }>(`/renders/${id}/generate`, {}),
};

export const renderSeriesApi = {
  list: (estimateId: string, roomId: string) =>
    api.get<{ items: RenderSeries[] }>(`/estimates/${estimateId}/render-series?roomId=${encodeURIComponent(roomId)}`),
  create: (estimateId: string, input: RenderSeriesCreateInput) =>
    api.post<{ series: RenderSeries }>(`/estimates/${estimateId}/render-series`, input),
  /** Фото — data URL JPEG, уже уменьшенное compressPhoto. */
  addPhoto: (seriesId: string, image: string) =>
    api.post<{ render: RoomRender }>(`/render-series/${seriesId}/photos`, { image }),
  approve: (seriesId: string) =>
    api.post<{ submitted: number; failed: number }>(`/render-series/${seriesId}/approve`, {}),
  setInProposal: (seriesId: string, inProposal: boolean) =>
    api.patch<{ count: number }>(`/render-series/${seriesId}`, { inProposal }),
  remove: (seriesId: string) => api.delete<void>(`/render-series/${seriesId}`),
};

export function isRenderPending(render: RoomRender): boolean {
  return render.status === 'PENDING' || render.status === 'SAVING';
}

/**
 * Картинка визуализации как object URL. Blob кэшируется react-query: файл по
 * id и версии не меняется, повторное открытие галереи не качает его заново.
 */
export function useRenderImage(renderId: string | null, variant: 'result' | 'source' = 'result', version?: string | null) {
  // Версия нужна в адресе, а не только в ключе: ответ API браузер кэширует
  // навечно, и после перегенерации прежний адрес отдал бы старую картинку.
  const v = variant === 'result' && version ? `&v=${encodeURIComponent(version)}` : '';
  const blob = useQuery({
    queryKey: ['render-image', renderId, variant, version ?? null],
    queryFn: () => apiBlob(`/renders/${renderId}/image?variant=${variant}${v}`),
    enabled: !!renderId,
    staleTime: Infinity,
  });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob.data) return;
    const objectUrl = URL.createObjectURL(blob.data);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob.data]);
  return { url, isError: blob.isError };
}
